import React from 'react';
import { Dropdown, Spinner, Button } from 'react-bootstrap';
import { BsArrowRepeat, BsPlay } from 'react-icons/bs';
import { IoMdArrowDropdown } from 'react-icons/io';
import { useSyncStatus, useInitiateProductSync } from '../../api/product-sync';
import { useFetchStore } from '../../api/account';
import styles from './Navbar.module.css';
import CustomBadge from '../common/CustomBadge';

const ProductSyncNotification = (props) => {
  const { showService = true } = props;
  const { data: syncStatusData, isLoading: isSyncLoading, refetch } = useSyncStatus();
  const { data: storeData, isLoading: isStoreLoading } = useFetchStore();
  const { mutate: initiateSync, isLoading: isInitiatingSync } = useInitiateProductSync();

  const isLoading = isSyncLoading || isStoreLoading;

  // Get status badge variant based on status
  const getStatusVariant = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'primary';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Get current store sync data
  const currentStoreSync = React.useMemo(() => {
    // Check if we have the required data
    if (!syncStatusData?.data?.data?.stores?.length) {
      return null;
    }

    // Simply use the first store in the response since the API call already filtered by store_id
    return syncStatusData.data.data.stores[0];
  }, [syncStatusData]);

  // Check if current store has active syncs
  const hasActiveSyncs = currentStoreSync?.active_syncs?.length > 0;

  // Count active syncs for current store
  const activeSyncsCount = currentStoreSync?.active_syncs?.length || 0;

  // Handle sync initiation
  const handleStartSync = () => {
    if (!storeData?.id) return;

    initiateSync(
      { store_id: storeData.id },
      {
        onSuccess: () => {
          refetch();
        },
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to start sync:', error);
        },
      }
    );
  };

  const renderSyncStatus = () => {
    if (!currentStoreSync) return null;

    if (currentStoreSync.active_syncs && currentStoreSync.active_syncs.length > 0) {
      return currentStoreSync.active_syncs.map((sync) => {
        const progress =
          sync.total_products > 0
            ? Math.round((sync.synced_products / sync.total_products) * 100)
            : 0;

        return (
          <div key={sync.id} className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <CustomBadge bg={getStatusVariant(sync.status)} className="me-2">
                {sync.status}
              </CustomBadge>
              <small>
                {sync.synced_products} / {sync.total_products}
              </small>
            </div>
            <div className="progress mt-1" style={{ height: '5px' }}>
              <div
                className={`progress-bar bg-${getStatusVariant(sync.status)}`}
                role="progressbar"
                style={{ width: `${progress}%` }}
                aria-valuenow={progress}
                aria-valuemin="0"
                aria-valuemax="100"
                aria-label={`Progress: ${progress}%`}
              >
                <span className="visually-hidden">Progress: {progress}%</span>
              </div>
            </div>
            <small className="text-muted d-block mt-1">
              Started: {formatDate(sync.started_at)}
            </small>
          </div>
        );
      });
    }
    if (currentStoreSync.last_completed_sync) {
      const lastSync = currentStoreSync.last_completed_sync;
      const progressValue = lastSync.total_products > 0
        ? Math.round((lastSync.synced_products / lastSync.total_products) * 100)
        : 0;

      return (
        <div className="mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <CustomBadge
              bg={getStatusVariant(lastSync.status)}
              className="me-2"
            >
              {lastSync.status}
            </CustomBadge>
            <small className="text-muted">
              Last Sync
            </small>
          </div>

          <div className="mb-2">
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">Total Products:</small>
              <small className="fw-bold">{lastSync.total_products || 0}</small>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">Synced:</small>
              <small className="fw-bold text-success">{lastSync.synced_products || 0}</small>
            </div>
            {lastSync.failed_products > 0 && (
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">Failed:</small>
                <small className="fw-bold text-danger">{lastSync.failed_products}</small>
              </div>
            )}
          </div>

          <div className="progress mb-2" style={{ height: '5px' }}>
            <div
              className={`progress-bar bg-${getStatusVariant(lastSync.status)}`}
              role="progressbar"
              style={{ width: `${progressValue}%` }}
              aria-valuenow={progressValue}
              aria-valuemin="0"
              aria-valuemax="100"
              aria-label={`Sync Progress: ${progressValue}%`}
            >
              <span className="visually-hidden">Sync Progress: {progressValue}%</span>
            </div>
          </div>

          <small className="text-muted d-block">
            Completed: {formatDate(lastSync.completed_at)}
          </small>

          {lastSync.error_message && (
            <small className="text-danger d-block mt-1">
              Error: {lastSync.error_message}
            </small>
          )}
        </div>
      );
    }
    return null;
  };
  // Render loading state
  const renderLoadingState = () => (
    <div className="text-center p-3">
      <Spinner animation="border" size="sm" />
      <p className="mb-0 mt-2">Loading sync status...</p>
    </div>
  );

  // Render sync content
  const renderSyncContent = () => (
    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
      <div className="px-3 py-2">
        {renderSyncStatus()}
      </div>
    </div>
  );

  // Render empty state
  const renderEmptyState = () => {
    let message = 'No sync information available';

    if (storeData?.integration_type) {
      message = `No sync information available for ${storeData.integration_type}`;
    } else if (storeData?.integration) {
      message = `No sync information available for ${storeData.integration}`;
    }

    return (
      <Dropdown.ItemText className="text-center text-muted p-2 w-100">
        {message}
      </Dropdown.ItemText>
    );
  };
  // Render dropdown content based on state
  const renderDropdownContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (currentStoreSync) {
      return renderSyncContent();
    }

    return renderEmptyState();
  };

  return (
    <Dropdown>
      <Dropdown.Toggle className={styles.dropdownToggle} variant="link">
        <div className="d-flex align-items-center">
          {isLoading ? (
            <Spinner animation="border" size="sm" className="me-2" />
          ) : (
            <BsArrowRepeat
              className={`me-2 fs-5 ${hasActiveSyncs ? 'text-primary' : ''}`}
            />
          )}
          {showService && <div className="d-none d-md-block">Product Sync</div>}
          {hasActiveSyncs && (
            <CustomBadge bg="primary" pill count className="ms-1">
              {activeSyncsCount}
            </CustomBadge>
          )}
          <IoMdArrowDropdown />
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu className={styles.syncDropdownMenu}>
        <Dropdown.Header>Product Synchronization</Dropdown.Header>

        {renderDropdownContent()}

        <Dropdown.Divider />
        <div className="px-3 py-2">
          <Button
            variant="primary"
            onClick={handleStartSync}
            disabled={isInitiatingSync || hasActiveSyncs}
            className="w-100"
            size="sm"
          >
            {isInitiatingSync ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Initiating Sync...
              </>
            ) : (
              <>
                <BsPlay className="me-2" />
                Sync Products
              </>
            )}
          </Button>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};
export default ProductSyncNotification;
